import { useState, useEffect } from 'react';
import { useWeb3Modal } from '@web3modal/wagmi/react';
import { useAccount, useDisconnect, useNetwork, useSwitchNetwork, useBalance } from 'wagmi';
import { formatAddress, clearProviderCache } from '../utils/web3';
import { WalletInfo } from '../types';

export const useWallet = () => {
  const { open } = useWeb3Modal();
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();
  const { chain } = useNetwork();
  const { switchNetwork } = useSwitchNetwork();
  const { data: balanceData } = useBalance({
    address: address,
    watch: true,
  });
  
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear provider cache when wallet or chain changes
  useEffect(() => {
    clearProviderCache();
  }, [address, chain?.id]);

  // Remove auto-connect logic - let users choose their wallet manually
  // This prevents conflicts with different wallet extensions

  // Connect wallet
  const connectWallet = async () => {
    setIsConnecting(true);
    setError(null);
    
    try {
      await open({ view: "Connect" });
      setIsConnecting(false);
      return true;
    } catch (err: any) {
      console.error('Error connecting wallet:', err);
      setError(err.message || 'Failed to connect wallet');
      setIsConnecting(false);
      return false;
    }
  };

  // Disconnect wallet
  const disconnectWallet = () => {
    disconnect();
  };

  // Change network
  const changeNetwork = async (chainId: string) => {
    if (!isConnected) {
      setError('Wallet not connected');
      return false;
    }

    try {
      const chainIdNumber = parseInt(chainId, 16);
      await switchNetwork?.(chainIdNumber);
      return true;
    } catch (err: any) {
      console.error('Error switching network:', err);
      setError(err.message || 'Failed to switch network');
      return false;
    }
  };

  // Format wallet info to match the existing interface
  const walletInfo: WalletInfo = {
    address: address || '',
    chainId: chain ? `0x${chain.id.toString(16)}` : '',
    isConnected: isConnected,
    balance: balanceData ? balanceData.formatted : '0',
  };

  // Format address for display
  const displayAddress = address ? formatAddress(address) : '';

  return {
    walletInfo,
    isConnecting,
    error,
    connectWallet,
    disconnectWallet,
    changeNetwork,
    displayAddress,
  };
};
